/**
 * URL utility functions for link processing
 */

/**
 * Convert absolute URL to relative path if it matches the home domain
 * Ignores protocol differences (http vs https)
 * 
 * @param {string} link - The absolute URL to process
 * @param {string} homeUrl - The home domain URL to compare against
 * @returns {string} - Relative path if domains match, otherwise original link
 */
export function convertToRelativeLink(link, homeUrl) {
  if (!link || !homeUrl) {
    return link || '';
  }

  try {
    // Extract domain part, ignoring protocol differences
    const homeUrlObj = new URL(homeUrl);
    const linkObj = new URL(link);
    
    // If domain matches (ignoring protocol), remove the domain part
    if (homeUrlObj.hostname === linkObj.hostname && homeUrlObj.port === linkObj.port) {
      let relativePath = linkObj.pathname + linkObj.search + linkObj.hash;
      
      // Special handling for *.twelve.la domains - remove one additional directory level
      if (linkObj.hostname.endsWith('.twelve.la')) {
        const pathParts = linkObj.pathname.split('/').filter(part => part !== '');
        if (pathParts.length > 0) {
          // Remove the first directory level and reconstruct the path
          pathParts.shift();
          relativePath = '/' + pathParts.join('/') + linkObj.search + linkObj.hash;
        }
      }
      
      return relativePath;
    } else {
      return link;
    }
  } catch (error) {
    // If URL parsing fails, return original link
    console.warn('URL parsing failed:', error);
    return link;
  }
}

/**
 * Check if two URLs have the same domain (ignoring protocol)
 * 
 * @param {string} url1 - First URL to compare
 * @param {string} url2 - Second URL to compare
 * @returns {boolean} - True if domains match, false otherwise
 */
export function isSameDomain(url1, url2) {
  if (!url1 || !url2) {
    return false;
  }

  try {
    const urlObj1 = new URL(url1);
    const urlObj2 = new URL(url2);
    
    return urlObj1.hostname === urlObj2.hostname && urlObj1.port === urlObj2.port;
  } catch (error) {
    console.warn('URL comparison failed:', error);
    return false;
  }
}